package com.solevo.dist.viewmodels

import com.solevo.dist.application.models.DeliveryNote
import com.solevo.dist.application.models.Order
import com.solevo.dist.application.models.Product
import com.solevo.dist.application.models.ProductItem
import com.solevo.dist.application.models.Transaction
import com.solevo.dist.application.models.toProductItem
import com.solevo.dist.network.api.invoice.InvoiceRemoteSource
import com.solevo.dist.network.api.orders.OrderRemoteSource
import com.solevo.dist.network.api.products.ProductRemoteSource
import com.solevo.dist.network.api.transactions.TransactionRemoteSource
import com.solevo.dist.network.api.user.UserRemoteSource
import com.solevo.dist.util.AmountFormatter
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.util.StatusMapper
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import org.koin.mp.KoinPlatform.getKoin
import kotlinx.datetime.Instant

enum class ProductsTab { All, Remaining, DeliveryNote, Invoices }

data class OrderDetailsUiState(
    val loading: Boolean = true,
    val error: String? = null,
    val selectedTab: ProductsTab = ProductsTab.All,
    val header: HeaderUi = HeaderUi(),
    val remainingItems: List<ProductItem> = emptyList(),
    val deliveryNotes: List<DeliveryNote> = emptyList(),
    val invoices: List<Transaction> = emptyList()
)

data class HeaderUi(
    val id: String = "",
    val statusText: String = "",
    val referenceText: String = "",
    val totalAmountText: String = "",
    val pickupText: String = "",
    val createdText: String = ""
)

class OrderDetailsViewModel(orderId: String) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val orderSource: OrderRemoteSource = getKoin().get()
    private val productSource: ProductRemoteSource = getKoin().get()
    private val invoiceSource: InvoiceRemoteSource = getKoin().get()
    private val transactionSource: TransactionRemoteSource = getKoin().get()
    private val userRemoteSource: UserRemoteSource = getKoin().get()

    private val _state = MutableStateFlow(OrderDetailsUiState())
    val state: StateFlow<OrderDetailsUiState> = _state.asStateFlow()

    init {
        start(orderId)
    }

    fun selectTab(tab: ProductsTab) {
        _state.value = _state.value.copy(selectedTab = tab)
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    private fun start(orderId: String) {
        println("Starting order details view model for order $orderId")
        _state.value = _state.value.copy(loading = true, error = null)

        scope.launch {
            // Wait for user to be authenticated before starting Firestore listeners
            // This ensures Firebase Auth session is restored from localStorage before queries
            userRemoteSource.currentUser()
                .filterNotNull()
                .flatMapLatest { 
                    println("User authenticated, starting order details fetch for order $orderId")
                    orderSource.getOrder(orderId)
                }
                .flatMapLatest { order ->
                    println("Order received: $order")
                    if (order == null) throw IllegalStateException("Order not found")

                    val ids = order.orderLines?.map { it.productId }.orEmpty()
                    val productsFlow =
                        if (ids.isNotEmpty()) productSource.getProducts(ids) else kotlinx.coroutines.flow.flow {
                            emit(emptyList())
                        }

                    val header = headerFrom(order)
                    productsFlow.map { products -> Pair(order, products to header) }
                }
                .flatMapLatest { (order, pair) ->
                    val (products, header) = pair
                    val remaining = getRemainingItems(order, products)

                    invoiceSource.getInvoicesForOrderLive(order.orderId).map { invoices ->
                        order to Triple(remaining, header, invoices)
                    }
                }
                .flatMapLatest { (order, triple) ->
                    val (remaining, header, invoices) = triple
                    val jobs = invoices.map {
                        async {
                            transactionSource.getTransactionByInvoiceIdLive(it.invoiceId).first()
                        }
                    }
                    val transactions = jobs.awaitAll()

                    kotlinx.coroutines.flow.flow {
                        emit(
                            Triple(
                                header,
                                remaining,
                                transactions to (order.deliveryNotes ?: emptyList())
                            )
                        )
                    }
                }
                .catch { t ->
                    _state.value =
                        _state.value.copy(loading = false, error = t.message ?: "Unknown error")
                }
                .collect { (header, remaining, pair) ->
                    val (transactions, deliveryNotes) = pair
                    _state.value = _state.value.copy(
                        loading = false,
                        error = null,
                        header = header,
                        remainingItems = remaining,
                        deliveryNotes = deliveryNotes,
                        invoices = transactions
                    )
                }
        }
    }

    private fun headerFrom(order: Order): HeaderUi {
        val remaining = order.totalRemainingAmount ?: order.totalAmount
        val statusInfo = StatusMapper.mapOrderStatus(order.status)
        return HeaderUi(
            id = order.orderId,
            statusText = statusInfo.label,
            referenceText = order.orderReference ?: "—",
            totalAmountText = AmountFormatter.formatAmountWithCurrency(remaining),
            pickupText = DateFormatter.formatDateOrDash(order.pickupDateTo),
            createdText = DateFormatter.formatDateOrDash(order.orderDate)
        )
    }

    private fun getRemainingItems(order: Order, products: List<Product>): List<ProductItem> {
        return order.orderLines?.map { remainingItem ->
            val product = products.firstOrNull { remainingItem.productId == it.productId }
            remainingItem.toProductItem(product?.getProductName() ?: remainingItem.id ?: "")
        }?.sortedBy { it.lineNumber.toInt() } ?: emptyList()
    }
}


